import { useEffect, useState } from 'react';
import { useVoiceStore } from '../../stores/voiceStore';
import {
  voiceService,
  REALTIME_VOICES,
  STT_PROVIDERS,
  TTS_PROVIDERS,
  PROVIDER_COST_HINT,
  SIP_PROVIDERS,
  type AiAgentState,
  type BookingSettings,
  type SipConfig,
  type TwilioConfig,
  type VoiceSettingsPatch,
  type VoiceTransport,
  type WhatsappSipDiagnosis,
  type WhatsappSipState,
} from './services/voiceService';

// Webhook URL admins paste into the Twilio Console. Derived from the same
// env vars the API client uses so it tracks dev / prod automatically.
const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ||
  `${import.meta.env.VITE_API_URL || ''}${import.meta.env.VITE_API_BASE_PATH || '/api'}`
).replace(/\/$/, '');
const TWILIO_VOICE_WEBHOOK_URL = `${API_BASE}/webhooks/twilio/voice`;

/**
 * Trigger the browser's microphone permission prompt and immediately
 * release the tracks. Once granted, subsequent getUserMedia calls (when
 * a real call arrives) resolve instantly. Without this, the very first
 * answered call hits the permission prompt and Meta times the call out
 * before the staff can click Allow → generate SDP answer → POST it back.
 */
async function preWarmMicPermission(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!navigator.mediaDevices?.getUserMedia) return 'unsupported';
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return 'granted';
  } catch {
    return 'denied';
  }
}

interface ToggleProps {
  checked: boolean;
  onClick: () => void;
}

function Toggle({ checked, onClick }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onClick}
      className={
        checked
          ? 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full bg-brand transition-colors focus:outline-none focus:ring-2 focus:ring-brand/40 focus:ring-offset-2 focus:ring-offset-white'
          : 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full bg-black/15 transition-colors focus:outline-none focus:ring-2 focus:ring-brand/40 focus:ring-offset-2 focus:ring-offset-white'
      }
    >
      <span
        className={
          checked
            ? 'inline-block h-5 w-5 translate-x-5 transform rounded-full bg-white shadow transition-transform'
            : 'inline-block h-5 w-5 translate-x-1 transform rounded-full bg-white shadow transition-transform'
        }
      />
    </button>
  );
}

export default function VoiceSettings() {
  const availableForCalls = useVoiceStore((s) => s.availableForCalls);
  const setAvailable = useVoiceStore((s) => s.setAvailable);
  const [micWarning, setMicWarning] = useState<string | null>(null);

  // Store-level settings (admin only on PATCH).
  const [recordingEnabled, setRecordingEnabled] = useState<boolean | null>(null);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [transport, setTransport] = useState<VoiceTransport | null>(null);
  const [sipCfg, setSipCfg] = useState<SipConfig | null>(null);
  const [sipDirty, setSipDirty] = useState(false);
  const [sipSaving, setSipSaving] = useState(false);
  const [sipSaved, setSipSaved] = useState(false);
  const [sipError, setSipError] = useState<string | null>(null);
  const [sipProvisioning, setSipProvisioning] = useState(false);
  const [sipProvisionMsg, setSipProvisionMsg] = useState<string | null>(null);
  const [didwwProvisioning, setDidwwProvisioning] = useState(false);
  const [didwwMsg, setDidwwMsg] = useState<string | null>(null);
  // Carrier inbound IPs edited as a comma/newline draft → array on save.
  const [sipIpsDraft, setSipIpsDraft] = useState('');
  // Jambonz SBC outbound IPs (DIDWW voice_out_trunk), comma/newline draft.
  const [sbcIpsDraft, setSbcIpsDraft] = useState('');
  const [twilioCfg, setTwilioCfg] = useState<TwilioConfig | null>(null);
  const [twilioDirty, setTwilioDirty] = useState(false);
  const [twilioSaving, setTwilioSaving] = useState(false);
  const [twilioError, setTwilioError] = useState<string | null>(null);
  const [twilioSaved, setTwilioSaved] = useState(false);

  // WhatsApp-via-Twilio state machine
  const [whatsappSip, setWhatsappSip] = useState<WhatsappSipState | null>(null);
  const [waSipBusy, setWaSipBusy] = useState(false);
  const [waSipError, setWaSipError] = useState<string | null>(null);

  // V2 AI voice agent (off by default; admin opt-in per store).
  const [aiAgent, setAiAgent] = useState<AiAgentState | null>(null);
  const [aiAgentError, setAiAgentError] = useState<string | null>(null);

  // Appointment booking (store-wide; off by default). Editable as a
  // comma/newline-separated draft, committed to the services[] array on blur.
  const [booking, setBooking] = useState<BookingSettings | null>(null);
  const [servicesDraft, setServicesDraft] = useState('');
  const [bookingError, setBookingError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    voiceService
      .getBookingSettings()
      .then((b) => {
        if (cancelled) return;
        setBooking(b);
        setServicesDraft(b.services.join(', '));
      })
      .catch(() => {
        if (cancelled) return;
        setBooking({ enabled: false, services: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    voiceService
      .getSettings()
      .then((s) => {
        if (cancelled) return;
        setRecordingEnabled(s.recording_enabled);
        setTransport(s.transport);
        setTwilioCfg(s.twilio);
        setSipCfg(s.sip);
        setSipIpsDraft((s.sip?.carrier_ip_allowlist || []).join(', '));
        setSbcIpsDraft((s.sip?.jambonz_sbc_ips || []).join(', '));
        setWhatsappSip(s.whatsapp_sip);
        setAiAgent(s.ai_agent ?? { enabled: false });
      })
      .catch(() => {
        if (cancelled) return;
        setRecordingEnabled(true);
        setTransport('webrtc');
        setTwilioCfg({
          account_sid: '',
          auth_token: '',
          api_key_sid: '',
          api_key_secret: '',
          phone_number: '',
          sip_domain: '',
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAvailableToggle = async () => {
    const next = !availableForCalls;
    setAvailable(next);
    if (next) {
      const result = await preWarmMicPermission();
      if (result === 'denied') {
        setMicWarning(
          "Microphone access is blocked. You won't be able to answer calls until you allow it — click the lock/mic icon in your browser's address bar.",
        );
      } else {
        setMicWarning(null);
      }
    } else {
      setMicWarning(null);
    }
  };

  const handleRecordingToggle = async () => {
    if (recordingEnabled === null) return;
    const next = !recordingEnabled;
    setRecordingEnabled(next);
    setRecordingError(null);
    try {
      await voiceService.updateSettings({ recording_enabled: next });
    } catch (err) {
      // Revert and surface the error (most likely 403 for non-admin).
      setRecordingEnabled(!next);
      const message = err instanceof Error ? err.message : 'Failed to update setting';
      setRecordingError(
        /403|forbidden/i.test(message)
          ? 'Only admins can change call recording for this store.'
          : message,
      );
    }
  };

  const handleTransport = async (next: VoiceTransport) => {
    if (transport === next) return;
    const prev = transport;
    setTransport(next);
    try {
      await voiceService.updateSettings({ transport: next });
    } catch (err) {
      setTransport(prev);
      const message = err instanceof Error ? err.message : 'Failed to switch transport';
      setTwilioError(
        /403|forbidden/i.test(message)
          ? 'Only admins can change the voice transport.'
          : message,
      );
    }
  };

  const updateSipField = (field: keyof SipConfig, value: string) => {
    if (!sipCfg) return;
    setSipCfg({ ...sipCfg, [field]: value });
    setSipDirty(true);
    setSipSaved(false);
    setSipError(null);
  };

  const handleSipSave = async () => {
    if (!sipCfg) return;
    setSipSaving(true);
    setSipError(null);
    setSipSaved(false);
    const patch: VoiceSettingsPatch['sip'] = {
      provider: sipCfg.provider,
      did: sipCfg.did,
      jambonz_base_url: sipCfg.jambonz_base_url,
      jambonz_account_sid: sipCfg.jambonz_account_sid,
      carrier_sip_realm: sipCfg.carrier_sip_realm,
      carrier_username: sipCfg.carrier_username,
      carrier_ip_allowlist: sipIpsDraft
        .split(/[\n,]/)
        .map((x) => x.trim())
        .filter(Boolean),
      jambonz_sip_fqdn: sipCfg.jambonz_sip_fqdn,
      jambonz_sbc_ips: sbcIpsDraft
        .split(/[\n,]/)
        .map((x) => x.trim())
        .filter(Boolean),
    };
    if (sipCfg.didww_api_key && sipCfg.didww_api_key !== '***') {
      patch.didww_api_key = sipCfg.didww_api_key;
    }
    // Only send secrets when the admin actually typed a new one (not '***').
    if (sipCfg.jambonz_api_key && sipCfg.jambonz_api_key !== '***') {
      patch.jambonz_api_key = sipCfg.jambonz_api_key;
    }
    if (sipCfg.carrier_password && sipCfg.carrier_password !== '***') {
      patch.carrier_password = sipCfg.carrier_password;
    }
    try {
      const updated = await voiceService.updateSettings({ sip: patch });
      setSipCfg(updated.sip);
      setSipDirty(false);
      setSipSaved(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save SIP settings';
      setSipError(
        /403|forbidden/i.test(message)
          ? 'Only admins can change SIP settings.'
          : message,
      );
    } finally {
      setSipSaving(false);
    }
  };

  const handleConfigureSip = async () => {
    setSipProvisioning(true);
    setSipProvisionMsg(null);
    setSipError(null);
    try {
      const res = await voiceService.configureSip();
      const p = res.provisioned;
      setSipProvisionMsg(
        `Provisioned in Jambonz (${p.auth} auth): application, carrier, ` +
          `${p.gateways_created} gateway(s), DID ${p.did}.`,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Provisioning failed';
      setSipError(
        /403|forbidden/i.test(message)
          ? 'Only admins can provision SIP.'
          : message,
      );
    } finally {
      setSipProvisioning(false);
    }
  };

  const handleConfigureDidww = async () => {
    setDidwwProvisioning(true);
    setDidwwMsg(null);
    setSipError(null);
    try {
      const res = await voiceService.configureDidww();
      const p = res.provisioned;
      setDidwwMsg(`DIDWW ${p.did} now routes to ${p.delivery_host} (trunk + group + DID assigned).`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'DIDWW provisioning failed';
      setSipError(
        /403|forbidden/i.test(message) ? 'Only admins can provision DIDWW.' : message,
      );
    } finally {
      setDidwwProvisioning(false);
    }
  };

  const updateTwilioField = (field: keyof TwilioConfig, value: string) => {
    if (!twilioCfg) return;
    setTwilioCfg({ ...twilioCfg, [field]: value });
    setTwilioDirty(true);
    setTwilioSaved(false);
    setTwilioError(null);
  };

  const handleTwilioSave = async () => {
    if (!twilioCfg) return;
    setTwilioSaving(true);
    setTwilioError(null);
    setTwilioSaved(false);
    // Build a partial patch — only include fields the user actually edited.
    // For secrets ('***'), the backend ignores echoed-back masked values.
    const patch: VoiceSettingsPatch['twilio'] = {
      account_sid: twilioCfg.account_sid,
      auth_token: twilioCfg.auth_token,
      api_key_sid: twilioCfg.api_key_sid,
      api_key_secret: twilioCfg.api_key_secret,
      phone_number: twilioCfg.phone_number,
      sip_domain: twilioCfg.sip_domain,
    };
    try {
      const updated = await voiceService.updateSettings({ twilio: patch });
      setTwilioCfg(updated.twilio);
      setTwilioDirty(false);
      setTwilioSaved(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save Twilio settings';
      setTwilioError(
        /403|forbidden/i.test(message)
          ? 'Only admins can change Twilio credentials.'
          : message,
      );
    } finally {
      setTwilioSaving(false);
    }
  };

  const runWaSip = async (op: () => Promise<{ whatsapp_sip: WhatsappSipState }>) => {
    setWaSipBusy(true);
    setWaSipError(null);
    try {
      const result = await op();
      setWhatsappSip(result.whatsapp_sip);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Operation failed';
      setWaSipError(
        /403|forbidden/i.test(message)
          ? 'Only admins can change this setting.'
          : message,
      );
    } finally {
      setWaSipBusy(false);
    }
  };

  const handleAiAgentToggle = async () => {
    if (aiAgent === null) return;
    const next = !aiAgent.enabled;
    const prev = aiAgent;
    setAiAgent({ ...aiAgent, enabled: next });
    setAiAgentError(null);
    try {
      const updated = await voiceService.updateSettings({
        ai_agent: { enabled: next },
      });
      setAiAgent(updated.ai_agent);
    } catch (err) {
      setAiAgent(prev);
      const message = err instanceof Error ? err.message : 'Failed to update setting';
      setAiAgentError(
        /403|forbidden/i.test(message)
          ? 'Only admins can change the AI voice agent setting.'
          : message,
      );
    }
  };

  // Generic patch helper for the per-field ai_agent inputs (voice,
  // greeting, max_call_duration_seconds). Optimistic update with
  // rollback on failure, matches the existing toggle pattern.
  const updateAiAgentField = async (patch: Partial<AiAgentState>) => {
    if (aiAgent === null) return;
    const prev = aiAgent;
    const next = { ...aiAgent, ...patch };
    setAiAgent(next);
    setAiAgentError(null);
    try {
      const updated = await voiceService.updateSettings({ ai_agent: patch });
      setAiAgent(updated.ai_agent);
    } catch (err) {
      setAiAgent(prev);
      const message = err instanceof Error ? err.message : 'Failed to update setting';
      setAiAgentError(
        /403|forbidden/i.test(message)
          ? 'Only admins can change the AI voice agent setting.'
          : message,
      );
    }
  };

  const updateBooking = async (patch: Partial<BookingSettings>) => {
    if (booking === null) return;
    const prev = booking;
    setBooking({ ...booking, ...patch });
    setBookingError(null);
    try {
      const updated = await voiceService.updateBookingSettings(patch);
      setBooking(updated);
      setServicesDraft(updated.services.join(', '));
    } catch (err) {
      setBooking(prev);
      const message = err instanceof Error ? err.message : 'Failed to update setting';
      setBookingError(
        /403|forbidden/i.test(message)
          ? 'Only admins can change booking settings.'
          : message,
      );
    }
  };

  const handleBookingToggle = () => {
    if (booking === null) return;
    void updateBooking({ enabled: !booking.enabled });
  };

  const commitServices = () => {
    if (booking === null) return;
    const services = servicesDraft
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    void updateBooking({ services });
  };

  const handleWaSipConfigure = () =>
    runWaSip(() => voiceService.configureWhatsappSip());
  const handleWaSipToggle = (active: boolean) =>
    runWaSip(() => voiceService.toggleWhatsappSip(active));
  const handleWaSipDelete = () => {
    if (
      !window.confirm(
        'Remove WhatsApp SIP configuration? Meta will switch back to WebRTC, ' +
          'the Twilio credential will be deleted, and you will need to re-run ' +
          'configure to re-enable.',
      )
    ) {
      return;
    }
    void runWaSip(() => voiceService.deleteWhatsappSip());
  };

  return (
    <div className="space-y-6">
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <h3 className="text-lg font-semibold text-ink">Voice calling</h3>
        <p className="mt-1 text-sm text-ink-2">
          Get notified when a customer calls in via WhatsApp. Calls only ring on
          this device when you're available — turn this off to silence the
          ringer without going offline.
        </p>

        <div className="mt-4 flex items-center justify-between rounded-card border border-line bg-surface-2 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-ink">Available for calls</p>
            <p className="text-xs text-ink-3">
              {availableForCalls
                ? "You'll see incoming-call toasts on this device."
                : 'Incoming-call toasts are silenced on this device.'}
            </p>
          </div>
          <Toggle checked={availableForCalls} onClick={handleAvailableToggle} />
        </div>

        {micWarning && (
          <div className="mt-3 rounded-card border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
            {micWarning}
          </div>
        )}
      </div>

      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <h3 className="text-lg font-semibold text-ink">Call recording</h3>
        <p className="mt-1 text-sm text-ink-2">
          Store-wide setting. When enabled, the staff browser records each
          answered call and uploads it to the conversation timeline along with
          a transcript.
        </p>

        <div className="mt-4 flex items-center justify-between rounded-card border border-line bg-surface-2 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-ink">Record &amp; transcribe calls</p>
            <p className="text-xs text-ink-3">
              {recordingEnabled === null
                ? 'Loading…'
                : recordingEnabled
                  ? 'Recordings + transcripts appear in the conversation after each call.'
                  : 'Calls are NOT recorded. Existing recordings are kept.'}
            </p>
          </div>
          <Toggle
            checked={!!recordingEnabled}
            onClick={handleRecordingToggle}
          />
        </div>

        {recordingError && (
          <div className="mt-3 rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
            {recordingError}
          </div>
        )}
      </div>

      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <h3 className="text-lg font-semibold text-ink">Voice transport</h3>
        <p className="mt-1 text-sm text-ink-2">
          How customer calls reach this dashboard.{' '}
          <strong className="text-ink">WebRTC</strong> is the free default
          (Meta routes WhatsApp calls directly to the staff browser).{' '}
          <strong className="text-ink">Twilio</strong> adds a media bridge
          (hot transfer + PSTN), and{' '}
          <strong className="text-ink">SIP</strong> routes a phone number
          through a Jambonz SBC — the path for Nigerian / non-Twilio carriers
          (ipnx, didww).
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <button
            type="button"
            onClick={() => handleTransport('webrtc')}
            disabled={transport === null}
            className={
              transport === 'webrtc'
                ? 'rounded-card border-2 border-brand bg-brand/10 p-3 text-left'
                : 'rounded-card border border-line bg-surface-2 p-3 text-left hover:border-line-2'
            }
          >
            <div className="text-sm font-medium text-ink">WebRTC</div>
            <div className="mt-0.5 text-xs text-ink-3">
              Direct from Meta to browser. Free.
            </div>
          </button>
          <button
            type="button"
            onClick={() => handleTransport('twilio')}
            disabled={transport === null}
            className={
              transport === 'twilio'
                ? 'rounded-card border-2 border-brand bg-brand/10 p-3 text-left'
                : 'rounded-card border border-line bg-surface-2 p-3 text-left hover:border-line-2'
            }
          >
            <div className="text-sm font-medium text-ink">Twilio</div>
            <div className="mt-0.5 text-xs text-ink-3">
              Media bridge. Hot transfer + PSTN. Paid.
            </div>
          </button>
          <button
            type="button"
            onClick={() => handleTransport('sip')}
            disabled={transport === null}
            className={
              transport === 'sip'
                ? 'rounded-card border-2 border-brand bg-brand/10 p-3 text-left'
                : 'rounded-card border border-line bg-surface-2 p-3 text-left hover:border-line-2'
            }
          >
            <div className="text-sm font-medium text-ink">SIP (Jambonz)</div>
            <div className="mt-0.5 text-xs text-ink-3">
              ipnx / didww trunk. Nigerian PSTN.
            </div>
          </button>
        </div>
      </div>

      {transport === 'sip' && sipCfg && (
        <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
          <h3 className="text-lg font-semibold text-ink">SIP trunk (Jambonz)</h3>
          <p className="mt-1 text-sm text-ink-2">
            Point your carrier's SIP trunk at the Jambonz SBC, then enter the
            DID and Jambonz API details here. The carrier (ipnx, didww) is just
            a label — routing happens on the SBC.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                Carrier
              </label>
              <select
                value={sipCfg.provider || 'ipnx'}
                onChange={(e) => updateSipField('provider', e.target.value)}
                className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
              >
                {SIP_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <TwilioField
              label="DID (dialed number)"
              placeholder="+234..."
              value={sipCfg.did}
              onChange={(v) => updateSipField('did', v)}
            />
            <TwilioField
              label="Jambonz API base URL (must include /api on cloud)"
              placeholder="https://jambonz.cloud/api"
              value={sipCfg.jambonz_base_url}
              onChange={(v) => updateSipField('jambonz_base_url', v)}
            />
            <TwilioField
              label="Jambonz account SID"
              placeholder="account SID"
              value={sipCfg.jambonz_account_sid}
              onChange={(v) => updateSipField('jambonz_account_sid', v)}
            />
            <TwilioField
              label="Jambonz API key"
              placeholder={sipCfg.jambonz_api_key === '***' ? '•••• (saved)' : 'API key'}
              value={sipCfg.jambonz_api_key === '***' ? '' : sipCfg.jambonz_api_key}
              onChange={(v) => updateSipField('jambonz_api_key', v)}
            />
            <TwilioField
              label="Jambonz SIP FQDN (SBC realm — required for the softphone)"
              placeholder="yoursub.sip.jambonz.cloud"
              value={sipCfg.jambonz_sip_fqdn}
              onChange={(v) => updateSipField('jambonz_sip_fqdn', v)}
            />
          </div>

          <p className="mt-5 text-xs text-ink-3">
            <strong className="text-ink-2">Carrier registration</strong> — for
            ipnx / DIDWW-registered trunks (username + password). Leave blank for
            DIDWW IP-auth (provisioned from the DIDWW IP preset).
          </p>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TwilioField
              label="Carrier SIP realm / domain"
              placeholder="sip.ipnxtelecoms.com"
              value={sipCfg.carrier_sip_realm}
              onChange={(v) => updateSipField('carrier_sip_realm', v)}
            />
            <TwilioField
              label="Carrier username (SIP line)"
              placeholder="02096232186"
              value={sipCfg.carrier_username}
              onChange={(v) => updateSipField('carrier_username', v)}
            />
            <TwilioField
              label="Carrier password (passkey)"
              type="password"
              placeholder={sipCfg.carrier_password === '***' ? '•••• (saved)' : 'passkey'}
              value={sipCfg.carrier_password === '***' ? '' : sipCfg.carrier_password}
              onChange={(v) => updateSipField('carrier_password', v)}
            />
          </div>

          <div className="mt-3">
            <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
              Carrier inbound IPs (optional)
            </label>
            <p className="mt-1 text-xs text-ink-3">
              Comma/newline-separated. Some carriers (e.g. ipnx) require IP
              whitelisting on top of registration — add their inbound signalling
              IPs here and they become inbound SIP gateways in Jambonz.
            </p>
            <textarea
              value={sipIpsDraft}
              onChange={(e) => {
                setSipIpsDraft(e.target.value);
                setSipDirty(true);
                setSipSaved(false);
              }}
              placeholder="e.g. 41.x.x.x, 41.x.x.y"
              rows={2}
              className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSipSave}
              disabled={!sipDirty || sipSaving}
              className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-ink disabled:opacity-50"
            >
              {sipSaving ? 'Saving…' : 'Save SIP settings'}
            </button>
            {sipSaved && <span className="text-xs text-success">Saved.</span>}
            <button
              type="button"
              onClick={handleConfigureSip}
              disabled={sipProvisioning || sipDirty}
              title={sipDirty ? 'Save settings first' : 'Create the trunk in Jambonz'}
              className="rounded-pill border border-brand/40 px-4 py-2 text-sm font-medium text-brand hover:bg-brand/10 disabled:opacity-50"
            >
              {sipProvisioning ? 'Configuring…' : 'Configure in Jambonz'}
            </button>
          </div>

          {sipProvisionMsg && (
            <div className="mt-3 rounded-card border border-success/30 bg-success/10 px-3 py-2 text-xs text-success">
              {sipProvisionMsg}
            </div>
          )}
          {sipError && (
            <div className="mt-3 rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {sipError}
            </div>
          )}

          {sipCfg.provider === 'didww' && (
            <div className="mt-6 border-t border-line pt-5">
              <h4 className="text-sm font-semibold text-ink">DIDWW routing (optional)</h4>
              <p className="mt-1 text-xs text-ink-3">
                Point your DIDWW number at Jambonz automatically. Deliver to your
                jambonz.cloud SIP FQDN; the number must already exist in DIDWW.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TwilioField
                  label="DIDWW API key"
                  type="password"
                  placeholder={sipCfg.didww_api_key === '***' ? '•••• (saved)' : 'API key'}
                  value={sipCfg.didww_api_key === '***' ? '' : sipCfg.didww_api_key}
                  onChange={(v) => updateSipField('didww_api_key', v)}
                />
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                  Jambonz SBC outbound IPs (optional override)
                </label>
                <p className="mt-1 text-xs text-ink-3">
                  Auto-discovered from your SIP FQDN — only fill this to override.
                  Used to provision a DIDWW outbound trunk so staff can place
                  calls. ⚠️ Requires DIDWW outbound/termination enabled on the
                  account first ("Get Access to Outbound Trunks").
                </p>
                <textarea
                  value={sbcIpsDraft}
                  onChange={(e) => {
                    setSbcIpsDraft(e.target.value);
                    setSipDirty(true);
                    setSipSaved(false);
                  }}
                  placeholder="e.g. 3.4.5.6, 3.4.5.7"
                  rows={2}
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                />
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleConfigureDidww}
                  disabled={didwwProvisioning || sipDirty}
                  title={sipDirty ? 'Save settings first' : 'Route the DID to Jambonz via the DIDWW API'}
                  className="rounded-pill border border-brand/40 px-4 py-2 text-sm font-medium text-brand hover:bg-brand/10 disabled:opacity-50"
                >
                  {didwwProvisioning ? 'Configuring…' : 'Configure DIDWW routing'}
                </button>
              </div>
              {didwwMsg && (
                <div className="mt-3 rounded-card border border-success/30 bg-success/10 px-3 py-2 text-xs text-success">
                  {didwwMsg}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-ink">AI voice agent</h3>
            <p className="mt-1 text-sm text-ink-2">
              Have Pulse answer inbound calls automatically. The agent uses
              your knowledge base and ecommerce tools just like the chat bot,
              and hands off to staff when the customer asks or when it can't
              help. English (Nigerian) only at launch.
            </p>
          </div>
          <Toggle
            checked={!!aiAgent?.enabled}
            onClick={handleAiAgentToggle}
          />
        </div>

        {aiAgent?.enabled && (
          <div className="mt-5 space-y-4 border-t border-line pt-4">
            {/* Voice models — STT + TTS providers. These handle voice only;
                the LangGraph agent is the brain in every case. */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                  Speech-to-text
                </label>
                <select
                  value={aiAgent.stt_provider}
                  onChange={(e) =>
                    void updateAiAgentField({
                      stt_provider: e.target.value as AiAgentState['stt_provider'],
                    })
                  }
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                >
                  {STT_PROVIDERS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                      {p === 'openai' ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ink-3">
                  {PROVIDER_COST_HINT[aiAgent.stt_provider]} · transcription
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                  Text-to-speech
                </label>
                <select
                  value={aiAgent.tts_provider}
                  onChange={(e) =>
                    void updateAiAgentField({
                      tts_provider: e.target.value as AiAgentState['tts_provider'],
                    })
                  }
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                >
                  {TTS_PROVIDERS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                      {p === 'openai' ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ink-3">
                  {PROVIDER_COST_HINT[aiAgent.tts_provider]} ·{' '}
                  {aiAgent.tts_provider === 'elevenlabs'
                    ? 'premium voice quality'
                    : 'cheapest'}
                </p>
              </div>
            </div>

            {/* Voice selection — depends on the chosen TTS provider */}
            {aiAgent.tts_provider === 'elevenlabs' ? (
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                  ElevenLabs voice ID
                </label>
                <p className="mt-1 text-xs text-ink-3">
                  Paste the voice ID from your ElevenLabs account (Voices → ID).
                </p>
                <input
                  type="text"
                  value={aiAgent.elevenlabs_voice_id}
                  onChange={(e) =>
                    setAiAgent({ ...aiAgent, elevenlabs_voice_id: e.target.value })
                  }
                  onBlur={(e) =>
                    void updateAiAgentField({ elevenlabs_voice_id: e.target.value })
                  }
                  placeholder="e.g. 21m00Tcm4TlvDq8ikWAM"
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                />
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                  Agent voice
                </label>
                <p className="mt-1 text-xs text-ink-3">
                  Which OpenAI voice the agent uses. Try a couple to find one
                  that fits your store's brand.
                </p>
                <select
                  value={aiAgent.voice}
                  onChange={(e) => void updateAiAgentField({ voice: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                >
                  {REALTIME_VOICES.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Custom greeting */}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                Opening greeting (optional)
              </label>
              <p className="mt-1 text-xs text-ink-3">
                Instruction to the agent for the first thing it says. Leave
                empty for the default "Greet warmly and ask how to help".
              </p>
              <textarea
                value={aiAgent.greeting}
                onChange={(e) => setAiAgent({ ...aiAgent, greeting: e.target.value })}
                onBlur={(e) => void updateAiAgentField({ greeting: e.target.value })}
                placeholder="e.g. Greet the caller as a customer of [Store Name]. Ask if they are calling about an order or a product."
                rows={3}
                className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
              />
            </div>

            {/* Max duration */}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                Max call duration (minutes)
              </label>
              <p className="mt-1 text-xs text-ink-3">
                Hard cap. When reached, the agent says a brief goodbye and
                the conversation goes to staff. Prevents a misbehaving call
                from running up the bill.
              </p>
              <input
                type="number"
                min={1}
                max={60}
                value={Math.round(aiAgent.max_call_duration_seconds / 60)}
                onChange={(e) => {
                  const mins = parseInt(e.target.value, 10);
                  if (Number.isFinite(mins) && mins >= 1 && mins <= 60) {
                    setAiAgent({
                      ...aiAgent,
                      max_call_duration_seconds: mins * 60,
                    });
                  }
                }}
                onBlur={(e) => {
                  const mins = parseInt(e.target.value, 10);
                  if (Number.isFinite(mins) && mins >= 1 && mins <= 60) {
                    void updateAiAgentField({
                      max_call_duration_seconds: mins * 60,
                    });
                  }
                }}
                className="mt-2 w-32 rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
              />
            </div>
          </div>
        )}

        {aiAgentError && (
          <div className="mt-3 rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
            {aiAgentError}
          </div>
        )}
      </div>

      {/* Appointment booking — store-wide; off by default; all tenants. */}
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-ink">Appointment booking</h3>
            <p className="mt-1 text-sm text-ink-2">
              Let the agent take booking requests (on calls and in chat). Each
              request is recorded for staff to confirm. Works on any channel —
              not just voice.
            </p>
          </div>
          <Toggle checked={!!booking?.enabled} onClick={handleBookingToggle} />
        </div>

        {booking?.enabled && (
          <div className="mt-5 space-y-4 border-t border-line pt-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-ink-2">
                Bookable services
              </label>
              <p className="mt-1 text-xs text-ink-3">
                Comma- or newline-separated. The agent offers these when a
                caller asks to book. Leave empty to accept any request.
              </p>
              <textarea
                value={servicesDraft}
                onChange={(e) => setServicesDraft(e.target.value)}
                onBlur={commitServices}
                placeholder="e.g. Haircut, Colour, Consultation"
                rows={3}
                className="mt-2 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
              />
            </div>
          </div>
        )}

        {bookingError && (
          <div className="mt-3 rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
            {bookingError}
          </div>
        )}
      </div>

      {transport === 'twilio' && (
        <TwilioWebhookHelp url={TWILIO_VOICE_WEBHOOK_URL} />
      )}

      {transport === 'twilio' && twilioCfg && (
        <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
          <h3 className="text-lg font-semibold text-ink">Twilio credentials</h3>
          <p className="mt-1 text-sm text-ink-2">
            From your Twilio Console. Secrets are stored encrypted and never
            echoed back to the browser.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TwilioField
              label="Account SID"
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={twilioCfg.account_sid}
              onChange={(v) => updateTwilioField('account_sid', v)}
            />
            <TwilioField
              label="Auth Token"
              placeholder={twilioCfg.auth_token === '***' ? '••• stored' : 'Auth token'}
              value={twilioCfg.auth_token === '***' ? '' : twilioCfg.auth_token}
              onChange={(v) => updateTwilioField('auth_token', v)}
              type="password"
            />
            <TwilioField
              label="API Key SID"
              placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={twilioCfg.api_key_sid}
              onChange={(v) => updateTwilioField('api_key_sid', v)}
            />
            <TwilioField
              label="API Key Secret"
              placeholder={twilioCfg.api_key_secret === '***' ? '••• stored' : 'API key secret'}
              value={twilioCfg.api_key_secret === '***' ? '' : twilioCfg.api_key_secret}
              onChange={(v) => updateTwilioField('api_key_secret', v)}
              type="password"
            />
            <TwilioField
              label="Phone Number"
              placeholder="+1234567890"
              value={twilioCfg.phone_number}
              onChange={(v) => updateTwilioField('phone_number', v)}
            />
            <TwilioField
              label="SIP Domain (optional)"
              placeholder="pulse-test.sip.twilio.com"
              value={twilioCfg.sip_domain}
              onChange={(v) => updateTwilioField('sip_domain', v)}
            />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={handleTwilioSave}
              disabled={!twilioDirty || twilioSaving}
              className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              {twilioSaving ? 'Saving…' : 'Save Twilio settings'}
            </button>
            {twilioSaved && (
              <span className="text-xs text-success">✓ Saved</span>
            )}
          </div>

          {twilioError && (
            <div className="mt-3 rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {twilioError}
            </div>
          )}
        </div>
      )}

      {transport === 'twilio' && whatsappSip && (
        <WhatsappSipCard
          state={whatsappSip}
          busy={waSipBusy}
          error={waSipError}
          onConfigure={handleWaSipConfigure}
          onToggle={handleWaSipToggle}
          onDelete={handleWaSipDelete}
        />
      )}
    </div>
  );
}

interface WhatsappSipCardProps {
  state: WhatsappSipState;
  busy: boolean;
  error: string | null;
  onConfigure: () => void;
  onToggle: (active: boolean) => void;
  onDelete: () => void;
}

function WhatsappSipCard({
  state,
  busy,
  error,
  onConfigure,
  onToggle,
  onDelete,
}: WhatsappSipCardProps) {
  const isConfigured = !!state.configured_at;
  const [diag, setDiag] = useState<WhatsappSipDiagnosis | null>(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagError, setDiagError] = useState<string | null>(null);

  const runDiagnose = async () => {
    setDiagBusy(true);
    setDiagError(null);
    try {
      const result = await voiceService.diagnoseWhatsappSip();
      setDiag(result);
    } catch (err) {
      setDiagError(err instanceof Error ? err.message : 'Diagnose failed');
    } finally {
      setDiagBusy(false);
    }
  };

  const [ipAuthBusy, setIpAuthBusy] = useState(false);
  const [ipAuthMsg, setIpAuthMsg] = useState<string | null>(null);
  const [ipAuthError, setIpAuthError] = useState<string | null>(null);

  const runUseIpWhitelist = async () => {
    setIpAuthBusy(true);
    setIpAuthMsg(null);
    setIpAuthError(null);
    try {
      const { result } = await voiceService.useIpWhitelistAuth();
      const added = result.addresses_added.length;
      const already = result.addresses_already_present;
      setIpAuthMsg(
        `IP ACL ready: ${added} ranges added, ${already} already present. ` +
          `IP ACL attached: ${result.ip_acl_attached ? 'yes' : 'no'}. ` +
          `Credential list detached: ${result.credential_list_detached ? 'yes' : 'no'}.`,
      );
    } catch (err) {
      setIpAuthError(err instanceof Error ? err.message : 'IP whitelist setup failed');
    } finally {
      setIpAuthBusy(false);
    }
  };

  return (
    <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
      <h3 className="text-lg font-semibold text-ink">Route WhatsApp calls through Twilio</h3>
      <p className="mt-1 text-sm text-ink-2">
        Switches Meta's calling mode to SIP so WhatsApp calls flow through your
        Twilio SIP domain. Unlocks hot transfer, WhatsApp-number transfer, and
        Twilio-side recording for WhatsApp calls — the same machinery that
        handles your PSTN calls.
      </p>

      {!isConfigured ? (
        <div className="mt-4 flex items-center justify-between rounded-card border border-line bg-surface-2 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-ink">Not configured</p>
            <p className="text-xs text-ink-3">
              Click Configure to enable SIP on Meta + write credentials to Twilio.
            </p>
          </div>
          <button
            type="button"
            onClick={onConfigure}
            disabled={busy}
            className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Configuring…' : 'Configure'}
          </button>
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-center justify-between rounded-card border border-line bg-surface-2 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-ink">
                {state.active ? (
                  <span className="text-success">● Active</span>
                ) : (
                  <span className="text-ink-3">○ Inactive</span>
                )}
              </p>
              <p className="text-xs text-ink-3">
                {state.active
                  ? 'WhatsApp calls are routing through Twilio. Configured ' +
                    new Date(state.configured_at!).toLocaleDateString() + '.'
                  : 'WhatsApp calls are using Meta WebRTC direct. Credentials remain saved — toggle on to route through Twilio again.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onToggle(!state.active)}
              disabled={busy}
              className={
                state.active
                  ? 'rounded-pill bg-warning/15 px-4 py-2 text-sm font-medium text-warning ring-1 ring-warning/40 transition-colors hover:bg-warning/25 disabled:cursor-not-allowed disabled:opacity-50'
                  : 'rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-ink disabled:cursor-not-allowed disabled:opacity-50'
              }
            >
              {busy
                ? state.active
                  ? 'Disabling…'
                  : 'Enabling…'
                : state.active
                  ? 'Disable'
                  : 'Enable'}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={runDiagnose}
              disabled={diagBusy}
              className="text-xs text-ink-2 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {diagBusy ? 'Diagnosing…' : 'Diagnose'}
            </button>
            <span className="text-ink-3">·</span>
            <button
              type="button"
              onClick={onConfigure}
              disabled={busy}
              className="text-xs text-brand underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Resync credentials
            </button>
            <span className="text-ink-3">·</span>
            <button
              type="button"
              onClick={runUseIpWhitelist}
              disabled={ipAuthBusy}
              className="text-xs text-brand underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              title="Replace password auth with IP-whitelist auth on the SIP Domain. Use when SIP auth keeps failing with 32202 despite a correct password."
            >
              {ipAuthBusy ? 'Switching…' : 'Use IP whitelist auth'}
            </button>
            <span className="text-ink-3">·</span>
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              className="text-xs text-danger underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Delete configuration
            </button>
          </div>

          {state.auth_mode === 'ip_whitelist' && (
            <div className="mt-2 text-xs text-success">
              Auth mode: <span className="font-mono">IP whitelist</span> (Twilio
              accepts SIP traffic from Meta's known IP ranges; no password digest).
            </div>
          )}
          {ipAuthMsg && (
            <div className="mt-3 rounded-card border border-success/30 bg-success/10 px-3 py-2 text-xs text-success">
              {ipAuthMsg}
            </div>
          )}
          {ipAuthError && (
            <div className="mt-3 rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {ipAuthError}
            </div>
          )}

          {diag && <DiagnoseReport report={diag} />}
          {diagError && (
            <div className="mt-3 rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {diagError}
            </div>
          )}
        </>
      )}

      {error && (
        <div className="mt-3 rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
          {error}
        </div>
      )}
    </div>
  );
}

function DiagnoseReport({ report }: { report: WhatsappSipDiagnosis }) {
  const items: Array<{ ok: boolean; label: string; detail?: string }> = [
    {
      ok: report.meta.sip_status === 'ENABLED',
      label: 'Meta: SIP enabled',
      detail: report.meta.error || `status=${report.meta.sip_status ?? 'unknown'}`,
    },
    {
      ok: report.meta.has_password,
      label: 'Meta: SIP password issued',
    },
    {
      ok: report.twilio.credential_list_exists,
      label: 'Twilio: Credential List exists',
      detail: report.twilio.error || undefined,
    },
    {
      ok: report.twilio.credential_for_user_exists,
      label: `Twilio: credential for ${report.meta.expected_username || 'WhatsApp number'}`,
    },
    {
      ok: report.twilio.sip_domain_found,
      label: `Twilio: SIP Domain ${report.store_config.sip_domain || '(unset)'} exists`,
    },
    {
      ok: report.twilio.credential_list_attached_to_domain,
      label: 'Twilio: Credential List attached to SIP Domain',
    },
    {
      ok: report.twilio.voice_url_set,
      label: 'Twilio: SIP Domain Voice URL set',
      detail: report.twilio.voice_url
        ? `current: ${report.twilio.voice_url}`
        : `expected: ${report.twilio.expected_voice_url}`,
    },
  ];
  const allOk = items.every((i) => i.ok);
  return (
    <div
      className={
        'mt-3 rounded-card border px-3 py-2 text-xs ' +
        (allOk
          ? 'border-success/30 bg-success/10 text-success'
          : 'border-warning/30 bg-warning/10 text-warning')
      }
    >
      <p className="font-medium">
        {allOk
          ? 'Structural setup looks correct. If calls still fail, the password may be out of sync — click Resync credentials, then test again.'
          : 'Setup issues found:'}
      </p>
      <ul className="mt-2 space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className={item.ok ? 'text-success' : 'text-danger'}>
              {item.ok ? '✓' : '✗'}
            </span>
            <span>
              {item.label}
              {item.detail && (
                <span className="ml-1 text-ink-3">— {item.detail}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface TwilioWebhookHelpProps {
  url: string;
}

function TwilioWebhookHelp({ url }: TwilioWebhookHelpProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — user can still select+copy manually */
    }
  };

  return (
    <div className="rounded-panel border border-brand/30 bg-brand/5 p-6">
      <h3 className="text-lg font-semibold text-ink">Configure Twilio Console</h3>
      <p className="mt-1 text-sm text-ink-2">
        After saving credentials below, point your Twilio phone number's voice
        webhook here so incoming calls reach this dashboard.
      </p>

      <ol className="mt-4 list-decimal space-y-1.5 pl-5 text-sm text-ink-2">
        <li>
          Open{' '}
          <a
            href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand underline-offset-2 hover:underline"
          >
            Twilio Console → Phone Numbers → Manage → Active numbers
          </a>{' '}
          and click your number.
        </li>
        <li>
          Scroll to <span className="text-ink">Voice Configuration</span>.
        </li>
        <li>
          Set <span className="text-ink">"A call comes in"</span> →{' '}
          <span className="text-ink">Webhook</span> → paste the URL below →{' '}
          <span className="text-ink">HTTP POST</span>.
        </li>
        <li>Click <span className="text-ink">Save configuration</span> at the bottom.</li>
      </ol>

      <div className="mt-3">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-3">
          Webhook URL
        </p>
        <div className="flex items-center gap-2 rounded-card border border-line bg-surface-2 px-3 py-2">
          <code className="flex-1 break-all font-mono text-xs text-brand">
            {url}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            className="flex-shrink-0 rounded bg-surface border border-line px-2 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-black/[0.04]"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <p className="mt-3 text-xs text-ink-3">
        Test it: call your Twilio number from any phone — your dashboard
        should ring within ~2 seconds. If nothing happens, check the call's
        status in Twilio Console → Monitor → Logs → Calls.
      </p>
    </div>
  );
}

interface TwilioFieldProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'password';
}

function TwilioField({ label, placeholder, value, onChange, type = 'text' }: TwilioFieldProps) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-3">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
        className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
      />
    </label>
  );
}
